import { useEffect, useState } from "react";
import { api, fmtDateRelative } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
    Plus, ArrowRight, CheckCircle2, XCircle, UserCheck, Mail, UserX,
    FileText, MessageSquare, CheckSquare, FileCheck, CreditCard, ShieldCheck,
    Activity,
} from "lucide-react";

const EVENT_META = {
    "deal.created":             { icon: Plus,           label: "Deal created",             color: "text-blue-600" },
    "deal.stage_changed":       { icon: ArrowRight,     label: "Stage changed",             color: "text-indigo-600" },
    "deal.closed_won":          { icon: CheckCircle2,   label: "Deal closed (won)",         color: "text-green-600" },
    "deal.closed_lost":         { icon: XCircle,        label: "Deal closed (lost)",        color: "text-red-500" },
    "buyer.contacted":          { icon: UserCheck,      label: "Buyer contacted",           color: "text-teal-600" },
    "buyer.ioi_received":       { icon: Mail,           label: "IOI received",              color: "text-purple-600" },
    "buyer.disengaged":         { icon: UserX,          label: "Buyer disengaged",          color: "text-orange-500" },
    "document.uploaded":        { icon: FileText,       label: "Document uploaded",         color: "text-slate-600" },
    "note.added":               { icon: MessageSquare,  label: "Note added",                color: "text-neutral-600" },
    "task.completed":           { icon: CheckSquare,    label: "Task completed",            color: "text-emerald-600" },
    "engagement_letter.signed": { icon: FileCheck,      label: "Engagement letter signed",  color: "text-green-700" },
    "fee.milestone_paid":       { icon: CreditCard,     label: "Fee milestone paid",        color: "text-gold" },
    "aml.cdd_completed":        { icon: ShieldCheck,    label: "AML / CDD completed",       color: "text-blue-700" },
};

function PayloadSummary({ event_type, payload }) {
    if (!payload) return null;
    switch (event_type) {
        case "deal.stage_changed":
            return <span className="text-muted-foreground">{payload.from_stage} → {payload.to_stage}</span>;
        case "deal.closed_won":
            return <span className="text-muted-foreground">Fee {payload.fee ?? "—"} · {payload.sector}</span>;
        case "deal.closed_lost":
            return <span className="text-muted-foreground">{payload.reason ?? "No reason given"}</span>;
        case "note.added":
            return <span className="text-muted-foreground">{payload.word_count} word{payload.word_count !== 1 ? "s" : ""}</span>;
        case "task.completed":
            return <span className="text-muted-foreground">{payload.task_title}</span>;
        case "document.uploaded":
            return <span className="text-muted-foreground">{payload.doc_type} · {payload.access_level}</span>;
        case "fee.milestone_paid":
            return <span className="text-muted-foreground">{payload.milestone_kind} · {payload.amount}</span>;
        default:
            return null;
    }
}

function StructuredFeed({ dealId }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/activities/deal/${dealId}`)
            .then(r => setEvents(r.data))
            .catch(() => setEvents([]))
            .finally(() => setLoading(false));
    }, [dealId]);

    if (loading) return <div className="text-sm text-muted-foreground py-4">Loading events…</div>;
    if (events.length === 0) return <div className="text-sm text-muted-foreground">No structured events yet.</div>;

    return (
        <ul className="space-y-3" data-testid="structured-events-list">
            {events.map((e) => {
                const meta = EVENT_META[e.event_type] || { icon: Activity, label: e.event_type, color: "text-gray-500" };
                const Icon = meta.icon;
                return (
                    <li key={e.id} className="flex gap-3 pb-3 border-b border-border/60 last:border-0" data-testid={`event-${e.event_type}`}>
                        <div className={`mt-0.5 flex-shrink-0 ${meta.color}`}><Icon className="w-4 h-4" /></div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-medium">{meta.label}</span>
                                <span className="text-[11px] text-muted-foreground font-mono flex-shrink-0">{fmtDateRelative(e.created_at)}</span>
                            </div>
                            <div className="text-xs mt-0.5 leading-relaxed">
                                <PayloadSummary event_type={e.event_type} payload={e.payload} />
                            </div>
                            {e.actor_role && (
                                <div className="text-[10px] text-muted-foreground mt-0.5 uppercase tracking-wider">{e.actor_role}</div>
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

export default function TimelineTab({ dealId }) {
    const [items, setItems] = useState([]);
    const [note, setNote] = useState("");
    const [type, setType] = useState("note");
    const [activeView, setActiveView] = useState("events");

    const refresh = () => api.get(`/deals/${dealId}/activities`).then((r) => setItems(r.data));
    useEffect(() => { refresh(); }, [dealId]);

    const add = async () => {
        if (!note.trim()) return;
        await api.post(`/deals/${dealId}/activities`, { type, content: note });
        setNote("");
        refresh();
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 max-w-5xl">
            <div className="lg:col-span-2 border border-border rounded-md bg-white p-6">
                <div className="flex items-center gap-2 mb-4">
                    <button
                        onClick={() => setActiveView("events")}
                        className={`text-xs px-3 py-1.5 rounded-sm font-medium transition-colors ${activeView === "events" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
                        data-testid="timeline-tab-events"
                    >
                        Events
                    </button>
                    <button
                        onClick={() => setActiveView("log")}
                        className={`text-xs px-3 py-1.5 rounded-sm font-medium transition-colors ${activeView === "log" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
                        data-testid="timeline-tab-log"
                    >
                        Activity log
                    </button>
                </div>

                {activeView === "events" ? (
                    <StructuredFeed dealId={dealId} />
                ) : (
                    <>
                        <h3 className="font-display font-bold tracking-tight mb-4">Activity log</h3>
                        <ul className="space-y-3" data-testid="activity-list">
                            {items.length === 0 && <li className="text-sm text-muted-foreground">No activity yet.</li>}
                            {items.map((a) => (
                                <li key={a.id} className="flex gap-3 pb-3 border-b border-border/60 last:border-0">
                                    <div className="w-1 bg-foreground/80 rounded-full flex-shrink-0" />
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between mb-1">
                                            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{a.type}</Badge>
                                            <span className="text-xs text-muted-foreground font-mono">{fmtDateRelative(a.created_at)}</span>
                                        </div>
                                        <p className="text-sm leading-relaxed">{a.content}</p>
                                        {a.user_name && <div className="text-[11px] text-muted-foreground mt-1">by {a.user_name}</div>}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
            <div className="border border-border rounded-md bg-white p-6 h-fit">
                <h3 className="font-display font-bold tracking-tight mb-4">Log activity</h3>
                <div className="space-y-3">
                    <Select value={type} onValueChange={setType}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="note">Note</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="meeting">Meeting</SelectItem>
                            <SelectItem value="call">Call</SelectItem>
                        </SelectContent>
                    </Select>
                    <Textarea rows={5} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What happened?" data-testid="activity-input" />
                    <Button onClick={add} className="w-full bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="log-activity-btn">
                        Log activity
                    </Button>
                </div>
            </div>
        </div>
    );
}
